import React, { useState, useEffect } from 'react';
import { collection, doc, setDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import CloseIcon from '../icons/CloseIcon';
import Button from './Button';

interface AddTrainingMaterialDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddTrainingMaterialDialog: React.FC<AddTrainingMaterialDialogProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [materialType, setMaterialType] = useState<'TSX' | 'URL' | 'PDF'>('TSX');
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState<'sharing' | 'scheduled'>('sharing');
  
  // TSX 타입일 경우
  const [tsxFileName, setTsxFileName] = useState('');
  
  // URL 타입일 경우
  const [url, setUrl] = useState('');
  
  // PDF 타입일 경우
  const [fileUrl, setFileUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // 다이얼로그가 열릴 때 초기화
  useEffect(() => {
    if (isOpen) {
      setMaterialType('TSX');
      setName('');
      setStartDate('');
      setEndDate('');
      setStatus('sharing');
      setTsxFileName('');
      setUrl('');
      setFileUrl('');
    }
  }, [isOpen]);

  // 다음 order 값 가져오기
  const getNextOrder = async (): Promise<number> => {
    try {
      const materialsRef = collection(db, 'aiEdutechTrainings');
      const q = query(materialsRef, orderBy('order', 'desc'));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return 1;
      }
      
      const lastOrder = querySnapshot.docs[0].data().order || 0;
      return lastOrder + 1;
    } catch (error) {
      console.error('Order 조회 오류:', error);
      return 1;
    }
  };

  // TSX 파일명에서 라우팅 경로 생성
  const generateTsxPath = (fileName: string): string => {
    // 파일명에서 확장자 제거하고 경로 생성
    const nameWithoutExt = fileName.replace(/\.tsx?$/i, '');
    return `/teacher-training/AIEdutech/${nameWithoutExt}`;
  };

  // 저장 핸들러
  const handleSave = async () => {
    // 유효성 검사
    if (!name.trim()) {
      alert('자료명을 입력해주세요.');
      return;
    }

    if (!startDate || !endDate) {
      alert('공유 기간을 선택해주세요.');
      return;
    }

    if (materialType === 'TSX' && !tsxFileName.trim()) {
      alert('TSX 파일명을 입력해주세요.');
      return;
    }

    if (materialType === 'URL' && !url.trim()) {
      alert('URL을 입력해주세요.');
      return;
    }

    if (materialType === 'PDF' && !fileUrl.trim()) {
      alert('PDF 파일을 업로드해주세요.');
      return;
    }

    try {
      setIsUploading(true);
      const materialsRef = collection(db, 'aiEdutechTrainings');
      const nextOrder = await getNextOrder();
      
      // 문서 ID 생성 (이름 기반)
      const docId = name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') + '-' + Date.now();

      const materialData: any = {
        name: name.trim(),
        type: materialType,
        sharePeriod: {
          start: startDate,
          end: endDate
        },
        uploadDate: new Date().toISOString().split('T')[0].replace(/-/g, '.'),
        status,
        order: nextOrder
      };

      // 타입별 추가 데이터
      if (materialType === 'TSX') {
        materialData.tsxPath = generateTsxPath(tsxFileName.trim());
      } else if (materialType === 'URL') {
        materialData.url = url.trim();
      } else if (materialType === 'PDF') {
        materialData.fileUrl = fileUrl.trim();
      }

      await setDoc(doc(materialsRef, docId), materialData);
      
      alert('✅ 교육 자료가 등록되었습니다!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('자료 등록 오류:', error);
      alert('❌ 자료 등록 중 오류가 발생했습니다.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Pretendard',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: '600',
            color: 'var(--Color-text-normal, #161616)'
          }}>
            교육 자료 업로드
          </div>
          <div
            onClick={onClose}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px'
            }}
          >
            <CloseIcon width={20} height={20} color="#989898" />
          </div>
        </div>

        {/* 내용 */}
        <div style={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* 자료 타입 선택 */}
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--Color-text-normal, #161616)',
              marginBottom: '8px'
            }}>
              자료 타입 *
            </div>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              {(['TSX', 'URL', 'PDF'] as const).map((type) => (
                <div
                  key={type}
                  onClick={() => setMaterialType(type)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${materialType === type ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-line-normal, #F0F0F0)'}`,
                    backgroundColor: materialType === type ? '#FFF5F0' : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: materialType === type ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                  }}
                >
                  {type === 'TSX' ? '연수 페이지' : type}
                </div>
              ))}
            </div>
          </div>

          {/* 자료명 */}
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--Color-text-normal, #161616)',
              marginBottom: '8px'
            }}>
              자료명 *
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="교육 자료명을 입력하세요"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid var(--Color-line-normal, #F0F0F0)',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                outline: 'none'
              }}
            />
          </div>

          {/* 타입별 입력 필드 */}
          {materialType === 'TSX' && (
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--Color-text-normal, #161616)',
                marginBottom: '8px'
              }}>
                TSX 파일명 *
              </div>
              <input
                type="text"
                value={tsxFileName}
                onChange={(e) => setTsxFileName(e.target.value)}
                placeholder="예: NewTraining.tsx"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  outline: 'none'
                }}
              />
              <div style={{
                fontSize: '12px',
                color: 'var(--Color-text-assistive, #989898)',
                marginTop: '4px'
              }}>
                라우팅 경로: {tsxFileName ? generateTsxPath(tsxFileName) : '/teacher-training/AIEdutech/...'}
              </div>
            </div>
          )}

          {materialType === 'URL' && (
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--Color-text-normal, #161616)',
                marginBottom: '8px'
              }}>
                URL *
              </div>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  outline: 'none'
                }}
              />
            </div>
          )}

          {materialType === 'PDF' && (
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--Color-text-normal, #161616)',
                marginBottom: '8px'
              }}>
                PDF 파일 URL *
              </div>
              <input
                type="url"
                value={fileUrl}
                onChange={(e) => setFileUrl(e.target.value)}
                placeholder="PDF 파일 URL을 입력하세요 (Firebase Storage 등)"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  outline: 'none'
                }}
              />
              <div style={{
                fontSize: '12px',
                color: 'var(--Color-text-assistive, #989898)',
                marginTop: '4px'
              }}>
                추후 파일 업로드 기능 추가 예정
              </div>
            </div>
          )}

          {/* 공유 기간 */}
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--Color-text-normal, #161616)',
              marginBottom: '8px'
            }}>
              공유 기간 *
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center'
            }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  outline: 'none'
                }}
              />
              <div style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '14px'
              }}>
                ~
              </div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* 상태 */}
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--Color-text-normal, #161616)',
              marginBottom: '8px'
            }}>
              상태 *
            </div>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              {(['sharing', 'scheduled'] as const).map((stat) => (
                <div
                  key={stat}
                  onClick={() => setStatus(stat)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${status === stat ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-line-normal, #F0F0F0)'}`,
                    backgroundColor: status === stat ? '#FFF5F0' : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: status === stat ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                  }}
                >
                  {stat === 'sharing' ? '공유 중' : '공유 예정'}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '20px 24px',
          borderTop: '1px solid var(--Color-line-normal, #F0F0F0)'
        }}>
          <Button
            variant="cancel"
            onClick={onClose}
            width="80px"
            height="40px"
          >
            취소
          </Button>
          <Button
            variant="submit"
            onClick={handleSave}
            width="80px"
            height="40px"
            disabled={isUploading}
          >
            {isUploading ? '등록 중...' : '등록'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddTrainingMaterialDialog;

